import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { YouTubeCandidate } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../../../common/types';
import { formatClock } from './addedAssetFacts';
import { addedAssetsMessages as messages } from './addedAssetsMessages';
import { youtubeThumbnailUrl } from './youtubeLinkState';
import styles from './YouTubeCandidateRow.module.scss';

interface YouTubeCandidateRowProps {
  candidate: YouTubeCandidate;
  catalogItemUuid: string;
  isPicked: boolean;
  onPick: (videoId: string) => void;
}

const PRIVACY = {
  public: messages.privacyPublic,
  unlisted: messages.privacyUnlisted,
  private: messages.privacyPrivate,
} as const;

/**
 * One video of the connected channel (design a2 `.ytrow`): the picture with its length, the title
 * with "Best match", the date, the views and the privacy, and a radio mark. A video that another
 * song already links says so.
 */
const YouTubeCandidateRowComponent: React.FC<YouTubeCandidateRowProps> = ({
  candidate,
  catalogItemUuid,
  isPicked,
  onPick,
}) => {
  const intl = useIntl();
  const length = formatClock(candidate.durationSec);
  const linkedElsewhere = candidate.linkedToSong !== null && candidate.linkedToSong !== catalogItemUuid;
  const facts = [
    candidate.publishedAt === null
      ? null
      : intl.formatDate(candidate.publishedAt, { day: 'numeric', month: 'short', year: 'numeric' }),
    candidate.views === null ? null : intl.formatMessage(messages.views, { count: candidate.views }),
    candidate.privacy === undefined ? null : intl.formatMessage(PRIVACY[candidate.privacy]),
    linkedElsewhere ? intl.formatMessage(messages.linkedElsewhere) : null,
  ].filter((fact): fact is string => fact !== null);

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isPicked}
      className={clsx(styles.ytRow, isPicked && styles.ytRowOn)}
      onClick={() => onPick(candidate.videoId)}
      data-testid={`youtube-candidate-${candidate.videoId}`}
    >
      <span className={styles.ytThumb}>
        <img src={candidate.thumbnailUrl ?? youtubeThumbnailUrl(candidate.videoId)} alt="" />
        {length !== null && <span className={styles.dur}>{length}</span>}
      </span>
      <span className={styles.ytText}>
        <b>
          {candidate.title}
          {candidate.bestMatch && <span className={styles.match}>{intl.formatMessage(messages.bestMatch)}</span>}
        </b>
        <small>{facts.join(' · ')}</small>
      </span>
      <span className={clsx(styles.radio, isPicked && styles.radioOn)} aria-hidden="true" />
    </button>
  );
};

export const YouTubeCandidateRow = TypedMemo(YouTubeCandidateRowComponent);
